import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { SkeletonModule } from 'primeng/skeleton';
import { TagModule } from 'primeng/tag';

import { CardContent } from '@/shared/components/card-content/card-content';
import { MedicoInconsistenciaResponse } from '@/core/api/sicom-api/registro/medico/inconsistencia/medico-inconsistencia.model';
import { MedicoInconsistenciaTotalUfResponse } from '@/core/api/sicom-api/registro/medico/inconsistencia/medico-inconsistencia-total-uf.model';
import { MedicoPendenciaResponse } from '@/core/api/sicom-api/registro/medico/pendencia/medico-pendencia.model';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { PainelLateral } from '@/shared/components/painel-lateral/painel-lateral';
import { MapaUf, type EstadoSelecionado } from '@/shared/components/mapa-uf/mapa-uf';
import { AuthService } from '@/core/auth/auth.service';
import { MedicoInconsistenciaFeatureService } from './medico-inconsistencia.service';
import { DetalhamentoPendencias } from '../detalhamento-pendencias/detalhamento-pendencias';
import { IndicadorGrupo, IndicadorPendencia } from '../mapa-pendencias/indicador-pendencia.model';
import { PendenciaLinha } from '../mapa-pendencias/pendencia-linha.model';
import { UfSelecaoGestaoService } from '../uf-selecao-gestao.service';
import { type PaginatorState } from 'primeng/paginator';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type InconsistenciaDescricaoTotal = MedicoInconsistenciaResponse | MedicoInconsistenciaTotalUfResponse;

interface IndicadorInconsistencia {
    id: string;
    titulo: string;
    descricao: string;
    grupo: IndicadorGrupo;
    classeGrupo: string;
    indice: number;
}

@Component({
    selector: 'app-inconsistencias-cadastro',
    standalone: true,
    imports: [DialogoPadraoDirective, CommonModule, SelectEstado, TagModule, CardModule, SkeletonModule, ButtonModule, DialogModule, MapaUf, DetalhamentoPendencias, CardContent, PainelLateral],
    templateUrl: './inconsistencias-cadastro.html',
    styleUrl: './inconsistencias-cadastro.scss'
})
export class InconsistenciasCadastro implements OnInit {
    readonly labels = ['Total Medicos', 'Inconsistencia Historico', 'Inconsistencia Especialidade', 'Inconsistencia Formacao', 'Inconsistencia Nascimento', 'Sem Sexo', 'Sem CPF'];

    readonly indicadores: IndicadorInconsistencia[] = [
        {
            id: 'total-medicos',
            titulo: 'Total de médicos',
            descricao: 'Base total de médicos ativos.',
            grupo: 'Base',
            classeGrupo: 'grupo-base',
            indice: 0
        },
        {
            id: 'historico',
            titulo: 'Inconsistências no histórico',
            descricao: 'Registros com inconsistências no histórico profissional.',
            grupo: 'Registro profissional',
            classeGrupo: 'grupo-registro',
            indice: 1
        },
        {
            id: 'especialidade',
            titulo: 'Inconsistências de especialidade',
            descricao: 'Registros com divergências em especialidades.',
            grupo: 'Registro profissional',
            classeGrupo: 'grupo-registro',
            indice: 2
        },
        {
            id: 'formacao',
            titulo: 'Inconsistências de formação',
            descricao: 'Registros com divergências em formação acadêmica.',
            grupo: 'Formação',
            classeGrupo: 'grupo-formacao',
            indice: 3
        },
        {
            id: 'nascimento',
            titulo: 'Inconsistências de nascimento',
            descricao: 'Registros com divergências de nascimento.',
            grupo: 'Dados pessoais',
            classeGrupo: 'grupo-dados',
            indice: 4
        },
        {
            id: 'sexo',
            titulo: 'Sexo não informado',
            descricao: 'Registros sem informação de sexo.',
            grupo: 'Dados pessoais',
            classeGrupo: 'grupo-dados',
            indice: 5
        },
        {
            id: 'cpf',
            titulo: 'CPF ausente ou inválido',
            descricao: 'Registros com CPF ausente ou inválido.',
            grupo: 'Dados pessoais',
            classeGrupo: 'grupo-dados',
            indice: 6
        }
    ];

    ufSelecionada: string | null = null;
    ufsPermitidas: string[] | null = null;
    private ufTokenRestrita: string | null = null;
    private ufConsulta: string | null = null;
    private valoresIndicadores: Array<number | null> = [];
    private atualizacaoId = 0;
    private readonly ufChange$ = new Subject<void>();
    private readonly analiticoChange$ = new Subject<void>();
    indicadorSelecionado: IndicadorInconsistencia | null = null;
    indicadorMaximizado: IndicadorInconsistencia | null = null;
    exibirModalMaximizado = false;
    linhasAnalitico: PendenciaLinha[] = [];
    totalRegistrosAnalitico = 0;
    firstAnalitico = 0;
    rowsAnalitico = 100;
    carregandoAnalitico = false;

    constructor(
        private inconsistenciasService: MedicoInconsistenciaFeatureService,
        private messageService: MessageService,
        private destroyRef: DestroyRef,
        private authService: AuthService,
        private ufSelecaoGestao: UfSelecaoGestaoService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.configurarUfPorToken();
        this.atualizarIndicadores();
    }

    private atualizarIndicadores(): void {
        this.ufChange$.next();
        this.ufConsulta = this.ufSelecionada;
        this.resetarIndicadores();
        this.voltarParaMapa();

        const requestId = this.iniciarAtualizacao();
        this.carregarTotais(requestId);
    }

    onUfSelecionadaChange(uf?: string | null): void {
        this.ufSelecionada = this.normalizarUfSelecionada(uf);
        this.ufSelecaoGestao.definir(this.ufSelecionada);

        this.atualizarIndicadores();
    }

    onMapaUfSelecionado(estado: EstadoSelecionado | null): void {
        this.onUfSelecionadaChange(estado?.uf ?? null);
    }

    private configurarUfPorToken(): void {
        const ufToken = this.authService.ufDoUsuario()?.trim().toUpperCase() ?? '';
        const ufTokenValida = /^[A-Z]{2}$/.test(ufToken) ? ufToken : '';

        if (ufTokenValida && ufTokenValida !== 'BR') {
            this.ufTokenRestrita = ufTokenValida;
            this.ufsPermitidas = [ufTokenValida];
            this.ufSelecionada = ufTokenValida;
            this.ufSelecaoGestao.definir(ufTokenValida);
            return;
        }

        this.ufTokenRestrita = null;
        this.ufsPermitidas = null;
        this.ufSelecionada = this.ufSelecaoGestao.uf();
    }

    private normalizarUfSelecionada(uf?: string | null): string | null {
        const ufNormalizada = String(uf ?? '')
            .trim()
            .toUpperCase();

        if (!ufNormalizada || ufNormalizada === 'BR') {
            return this.ufTokenRestrita;
        }

        if (!/^[A-Z]{2}$/.test(ufNormalizada)) {
            return this.ufTokenRestrita;
        }

        if (this.ufTokenRestrita && ufNormalizada !== this.ufTokenRestrita) {
            return this.ufTokenRestrita;
        }

        return ufNormalizada;
    }

    obterQuantidadeIndicador(indice: number): number | null {
        return this.valoresIndicadores[indice] ?? null;
    }

    selecionarIndicadorAnalitico(indicador: IndicadorInconsistencia): void {
        this.analiticoChange$.next();
        this.exibirModalMaximizado = false;
        this.indicadorMaximizado = null;
        this.indicadorSelecionado = indicador;
        this.firstAnalitico = 0;
        this.linhasAnalitico = [];
        this.totalRegistrosAnalitico = 0;
        this.carregarAnalitico();
    }

    maximizarIndicador(indicador: IndicadorInconsistencia): void {
        this.indicadorMaximizado = indicador;
        this.exibirModalMaximizado = true;
    }

    voltarParaMapa(): void {
        this.analiticoChange$.next();
        this.indicadorSelecionado = null;
        this.indicadorMaximizado = null;
        this.exibirModalMaximizado = false;
        this.linhasAnalitico = [];
        this.totalRegistrosAnalitico = 0;
        this.firstAnalitico = 0;
        this.carregandoAnalitico = false;
    }

    onPageChangeAnalitico(event: PaginatorState): void {
        this.analiticoChange$.next();
        this.firstAnalitico = event.first ?? 0;
        this.rowsAnalitico = event.rows ?? this.rowsAnalitico;
        this.carregarAnalitico();
    }

    obterIndicadorAnalitico(): IndicadorPendencia {
        const indicador = this.indicadorSelecionado;
        return {
            id: indicador?.id ?? '',
            titulo: indicador?.titulo ?? 'Inconsistências de Cadastro',
            descricao: indicador?.descricao ?? '',
            grupo: indicador?.grupo ?? 'Base',
            classeGrupo: indicador?.classeGrupo ?? 'grupo-base',
            quantidade: this.totalRegistrosAnalitico,
            icone: this.obterIconeIndicador(indicador)
        };
    }

    private carregarTotais(requestId: number): void {
        this.carregarTotalMedicos(requestId);
        this.carregarHistorico(requestId);
        this.carregarSexo(requestId);
        this.carregarEspecialidade(requestId);
        this.carregarNascimento(requestId);
        this.carregarCpf(requestId);
        this.carregarFormacao(requestId);
    }

    private carregarAnalitico(): void {
        if (!this.indicadorSelecionado) {
            return;
        }

        const indicadorId = this.indicadorSelecionado.id;
        const size = this.rowsAnalitico;
        const page = size > 0 ? Math.floor(this.firstAnalitico / size) : 0;
        this.carregandoAnalitico = true;
        this.inconsistenciasService
            .listarAnalitico(indicadorId, this.ufConsulta, page, size)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                takeUntil(this.ufChange$),
                takeUntil(this.analiticoChange$),
                // markForCheck: estado assíncrono gravado em campos não-signal; sob o layout OnPush o
                // tick da resposta HTTP não alcança este painel (só renderizava após um clique).
                finalize(() => {
                    if (this.indicadorSelecionado?.id === indicadorId) {
                        this.carregandoAnalitico = false;
                    }
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (resp) => {
                    if (this.indicadorSelecionado?.id !== indicadorId) {
                        return;
                    }

                    this.linhasAnalitico = (resp?.content ?? []).map((item) => this.mapLinhaAnalitico(item));
                    this.totalRegistrosAnalitico = resp?.totalElements ?? 0;
                },
                error: () => {
                    if (this.indicadorSelecionado?.id !== indicadorId) {
                        return;
                    }

                    this.linhasAnalitico = [];
                    this.totalRegistrosAnalitico = 0;
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Falha ao carregar os dados analíticos da inconsistência.'
                    });
                }
            });
    }

    private carregarTotalMedicos(requestId: number): void {
        this.inconsistenciasService
            .listarPorUf(this.ufConsulta)
            .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.ufChange$))
            .subscribe({
                next: (resp) => {
                    if (!this.isRequestAtual(requestId)) {
                        return;
                    }
                    this.atualizarValores(resp);
                    this.definirIndicadorPadrao(0);
                    this.cdr.markForCheck();
                },
                error: () => {
                    if (!this.isRequestAtual(requestId)) {
                        return;
                    }
                    this.marcarIndicadorErro(0);
                    this.cdr.markForCheck();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Falha ao obter o total de médicos.'
                    });
                }
            });
    }

    private carregarHistorico(requestId: number): void {
        this.inconsistenciasService
            .listarHistoricoPorUf(this.ufConsulta)
            .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.ufChange$))
            .subscribe({
                next: (resp) => {
                    if (!this.isRequestAtual(requestId)) {
                        return;
                    }
                    this.atualizarValores(resp);
                    this.definirIndicadorPadrao(1);
                    this.cdr.markForCheck();
                },
                error: () => {
                    if (!this.isRequestAtual(requestId)) {
                        return;
                    }
                    this.marcarIndicadorErro(1);
                    this.cdr.markForCheck();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Falha ao obter as inconsistências de histórico.'
                    });
                }
            });
    }

    private carregarSexo(requestId: number): void {
        this.inconsistenciasService
            .listarSexoPorUf(this.ufConsulta)
            .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.ufChange$))
            .subscribe({
                next: (resp) => {
                    if (!this.isRequestAtual(requestId)) {
                        return;
                    }
                    this.atualizarValores(resp);
                    this.definirIndicadorPadrao(5);
                    this.cdr.markForCheck();
                },
                error: () => {
                    if (!this.isRequestAtual(requestId)) {
                        return;
                    }
                    this.marcarIndicadorErro(5);
                    this.cdr.markForCheck();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Falha ao obter as inconsistências de sexo.'
                    });
                }
            });
    }

    private carregarEspecialidade(requestId: number): void {
        this.inconsistenciasService
            .listarEspecialidadePorUf(this.ufConsulta)
            .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.ufChange$))
            .subscribe({
                next: (resp) => {
                    if (!this.isRequestAtual(requestId)) {
                        return;
                    }
                    this.atualizarValores(resp);
                    this.definirIndicadorPadrao(2);
                    this.cdr.markForCheck();
                },
                error: () => {
                    if (!this.isRequestAtual(requestId)) {
                        return;
                    }
                    this.marcarIndicadorErro(2);
                    this.cdr.markForCheck();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Falha ao obter as inconsistências de especialidade.'
                    });
                }
            });
    }

    private carregarNascimento(requestId: number): void {
        this.inconsistenciasService
            .listarNascimentoPorUf(this.ufConsulta)
            .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.ufChange$))
            .subscribe({
                next: (resp) => {
                    if (!this.isRequestAtual(requestId)) {
                        return;
                    }
                    this.atualizarValores(resp);
                    this.definirIndicadorPadrao(4);
                    this.cdr.markForCheck();
                },
                error: () => {
                    if (!this.isRequestAtual(requestId)) {
                        return;
                    }
                    this.marcarIndicadorErro(4);
                    this.cdr.markForCheck();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Falha ao obter as inconsistências de nascimento.'
                    });
                }
            });
    }

    private carregarCpf(requestId: number): void {
        this.inconsistenciasService
            .listarCpfPorUf(this.ufConsulta)
            .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.ufChange$))
            .subscribe({
                next: (resp) => {
                    if (!this.isRequestAtual(requestId)) {
                        return;
                    }
                    this.atualizarValores(resp);
                    this.definirIndicadorPadrao(6);
                    this.cdr.markForCheck();
                },
                error: () => {
                    if (!this.isRequestAtual(requestId)) {
                        return;
                    }
                    this.marcarIndicadorErro(6);
                    this.cdr.markForCheck();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Falha ao obter as inconsistências de CPF.'
                    });
                }
            });
    }

    private carregarFormacao(requestId: number): void {
        this.inconsistenciasService
            .listarFormacaoPorUf(this.ufConsulta)
            .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.ufChange$))
            .subscribe({
                next: (resp) => {
                    if (!this.isRequestAtual(requestId)) {
                        return;
                    }
                    this.atualizarValores(resp);
                    this.definirIndicadorPadrao(3);
                    this.cdr.markForCheck();
                },
                error: () => {
                    if (!this.isRequestAtual(requestId)) {
                        return;
                    }
                    this.marcarIndicadorErro(3);
                    this.cdr.markForCheck();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Falha ao obter as inconsistências de formação.'
                    });
                }
            });
    }

    private atualizarValores(resposta: InconsistenciaDescricaoTotal[] | null | undefined): void {
        if (!resposta?.length) {
            return;
        }

        const indices = this.obterIndicesPorDescricao();
        const somasPorIndice = new Map<number, number>();

        resposta.forEach((item) => {
            if ('ufSgUf' in item && this.ufConsulta && item.ufSgUf && item.ufSgUf !== this.ufConsulta) {
                return;
            }

            const descricaoNormalizada = this.normalizarTexto(item.descricao ?? '');
            if (!descricaoNormalizada) {
                return;
            }

            let indice = indices.get(descricaoNormalizada);
            if (indice === undefined) {
                for (const [labelNormalizada, idx] of indices.entries()) {
                    if (descricaoNormalizada.includes(labelNormalizada) || labelNormalizada.includes(descricaoNormalizada)) {
                        indice = idx;
                        break;
                    }
                }
            }

            if (indice !== undefined) {
                somasPorIndice.set(indice, (somasPorIndice.get(indice) ?? 0) + (item.total ?? 0));
            }
        });

        somasPorIndice.forEach((total, indice) => {
            this.valoresIndicadores[indice] = total;
        });
    }

    private obterIndicesPorDescricao(): Map<string, number> {
        const indices = new Map<string, number>();
        this.labels.forEach((label, index) => {
            const normalizada = this.normalizarTexto(label);
            if (normalizada) {
                indices.set(normalizada, index);
            }

            this.obterSinonimos(index).forEach((sinonimo) => {
                const normalizado = this.normalizarTexto(sinonimo);
                if (normalizado) {
                    indices.set(normalizado, index);
                }
            });
        });
        return indices;
    }

    private obterSinonimos(indice: number): string[] {
        switch (indice) {
            case 0:
                return ['Total de Medicos'];
            case 1:
                return ['Inconsistencia no Historico', 'Historico divergente', 'Divergencias no historico'];
            case 2:
                return ['Inconsistencia de Especialidade', 'Registro de especialidades incompleto'];
            case 3:
                return ['Formacao inconsistente', 'Inconsistencia de Formacao'];
            case 4:
                return ['Nascimento divergente', 'Inconsistencia de Nascimento'];
            case 5:
                // 'Inconsistencia Sexo' e a descricao que a API devolve; sem ela o cartao fica zerado.
                return ['Inconsistencia Sexo', 'Genero nao informado', 'Sexo nao informado', 'Genero ausente'];
            case 6:
                // 'Inconsistencia CPF' e a descricao que a API devolve; sem ela o cartao fica zerado.
                return ['Inconsistencia CPF', 'CPF ausente', 'CPF invalido', 'CPF ausente ou invalido'];
            default:
                return [];
        }
    }

    private normalizarTexto(valor: string): string {
        return valor
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, ' ')
            .replace(/\b(de|da|do|dos|das|e)\b/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    }

    private criarSerieVazia(): Array<number | null> {
        return Array.from({ length: this.labels.length }, () => null);
    }

    private resetarIndicadores(): void {
        this.valoresIndicadores = this.criarSerieVazia();
    }

    private iniciarAtualizacao(): number {
        this.atualizacaoId += 1;
        return this.atualizacaoId;
    }

    private isRequestAtual(requestId: number): boolean {
        return requestId === this.atualizacaoId;
    }

    private definirIndicadorPadrao(indice: number): void {
        if (this.valoresIndicadores[indice] === null || this.valoresIndicadores[indice] === undefined) {
            this.valoresIndicadores[indice] = 0;
        }
    }

    private marcarIndicadorErro(indice: number): void {
        this.valoresIndicadores[indice] = null;
    }

    private mapLinhaAnalitico(item: MedicoPendenciaResponse): PendenciaLinha {
        return {
            uf: item.ufSgUf ?? '',
            crm: item.nuCrm ?? '',
            nome: item.nmSolicitante ?? '',
            situacao: item.dsSituacao ?? item.inSituacao ?? '',
            codigoSituacao: item.inSituacao ?? '',
            detalhe: item.informacaoComplementar ?? ''
        };
    }

    obterIconeIndicador(indicador?: IndicadorInconsistencia | null): string {
        if (!indicador) {
            return 'pi pi-exclamation-triangle';
        }

        if (indicador.grupo === 'Base') {
            return 'pi pi-chart-bar';
        }

        if (indicador.grupo === 'Registro profissional') {
            return 'pi pi-id-card';
        }

        if (indicador.grupo === 'Formação') {
            return 'pi pi-graduation-cap';
        }

        if (indicador.grupo === 'Dados pessoais') {
            return 'pi pi-user';
        }

        return 'pi pi-exclamation-triangle';
    }
}
